import React from 'react';
import { FormattedMessage } from 'react-intl';
import { StyledTableCellProps as StyledTableCell } from '../utils/TableStyles';
import Sort from '../../assets/images/llantas/table/sort.svg';
import SortAsc from '../../assets/images/llantas/table/sort-asc.svg';
import SortDesc from '../../assets/images/llantas/table/sort-desc.svg';

/**
 * Encabezado de columna ordenable para las grillas de React/MUI.
 *
 * Reemplaza el bloque de ~7 líneas que está copiado en 26 grillas —etiqueta, el `onClick`
 * con el campo, y el `<img>` con el ternario que elige entre los tres iconos— y con él los
 * tres imports de los SVG en cada archivo. Es puramente presentacional: no maneja el estado
 * del ordenamiento, solo lo muestra y avisa del click.
 *
 *     <SortableCell field="detalle" sort={sortQuery} onSort={requestSort}
 *                   messageId="ticketsGrid.column_detail.label_detalle" label="Detalle" />
 *
 * Los tres iconos y el tamaño (12x22) son los que ya usan las 26 grillas: el objetivo es que
 * la columna se vea exactamente igual que antes de migrar.
 */

const TAMANIO_ICONO = { height: '12px', width: '22px' };

/**
 * Dirección vigente de una columna. Acepta las DOS formas de estado que hay en el proyecto:
 *
 *  - `{ campo, direccion }`, el de `useGridState`: una sola columna ordenada;
 *  - un objeto con una clave por columna, `{ detalle: 'asc', estado: '' }`, que es como lo
 *    tienen las 26 grillas sin migrar (y como viaja al backend: `&detalleSort=asc`).
 *
 * Ojo con el nombre de las claves: la fase 1 soportaba `{ field, direction }` en inglés,
 * inventado antes de que existiera el hook. Cuando el hook llegó, en la fase 2, resultó estar
 * en español, así que el estado no coincidía con lo que esta función buscaba y el icono se
 * quedaba en "sin orden" para todas las columnas. Lo detectó la review del PR #2265, no los
 * tests: por eso ahora hay uno que cruza el hook con esta función.
 */
export const direccionDeOrden = (sort, field) => {
	if (!sort || !field) return '';
	if (typeof sort.campo === 'string') return sort.campo === field ? sort.direccion || '' : '';
	return sort[field] || '';
};

/** El icono que corresponde: sin orden, ascendente o descendente. */
export const iconoDeOrden = (direccion) => {
	if (direccion === 'asc') return SortAsc;
	if (direccion === 'desc') return SortDesc;
	return Sort;
};

/**
 * @param field      nombre de la columna, el que espera `onSort`
 * @param sort       estado del ordenamiento (ver `direccionDeOrden`)
 * @param onSort     (field) => void. Sin esta prop la columna no es ordenable: ni cursor,
 *                   ni click, ni icono.
 * @param messageId  key i18n de la etiqueta; `label` es su defaultMessage
 * @param children   alternativa a messageId/label, para un encabezado que no es solo texto
 * @param show       false oculta la columna (visibilidad por config o por el usuario)
 */
const SortableCell = (props) => {
	// Sin `onSort` la columna NO es ordenable: no lleva el cursor de mano, no engancha un
	// handler y no muestra el icono, que prometería un orden que no va a pasar. Sirve para
	// declarar todas las columnas igual y decidir después cuáles se pueden ordenar (p.ej.
	// las que el endpoint no soporta).
	const ordenable = typeof props.onSort === 'function';
	const direccion = ordenable ? direccionDeOrden(props.sort, props.field) : '';

	return (
		<StyledTableCell
			show={props.show}
			align={props.align || 'left'}
			className={props.className}
			style={ordenable ? Object.assign({ cursor: 'pointer' }, props.style) : props.style}
			onClick={ordenable ? () => props.onSort(props.field) : undefined}
		>
			{props.children ? (
				props.children
			) : (
				<FormattedMessage id={props.messageId} defaultMessage={props.label} />
			)}
			{ordenable && (
				<img className="ml-1" src={iconoDeOrden(direccion)} alt={'/'} style={TAMANIO_ICONO} />
			)}
		</StyledTableCell>
	);
};

export default SortableCell;
